import React from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../data/site";
import { SectionHeading } from "../components/dental/SectionHeading";
import { Seo } from "../components/Seo";
import { breadcrumbSchema } from "../data/seo";
import { openWeaveBookingInNewTab, WEAVE_BOOKING_URL } from "../lib/weaveBooking";

const treatmentIncludes = [
  "Dental implants",
  "Crowns and bridges",
  "Veneers",
  "Root canal therapy",
  "Periodontal (gum) treatment",
  "Tooth-colored fillings",
  "Bite correction",
  "Bone grafting",
  "Orthodontic treatment when appropriate",
];

const candidateFor = [
  "Multiple broken or worn teeth",
  "Several missing teeth",
  "Extensive decay",
  "Old failing dental work",
  "Severe tooth grinding (bruxism)",
  "Bite collapse or jaw discomfort",
  "Advanced gum disease",
  "Difficulty chewing comfortably",
  "Chronic dental pain",
  "Trauma affecting multiple teeth",
];

const evaluationIncludes = [
  "Comprehensive oral examination",
  "Digital X-Rays",
  "3D CBCT imaging",
  "Digital photographs",
  "Bite analysis",
  "Smile evaluation",
  "Periodontal assessment",
];

const correctedConditions = [
  "Worn teeth from years of grinding",
  "Broken or fractured teeth",
  "Missing teeth",
  "Collapsed bite",
  "Uneven tooth wear",
  "Severe discoloration",
  "Chronic infection",
  "Gum disease",
  "Poorly fitting crowns or bridges",
  "Functional bite problems",
];

const rehabilitationBenefits = [
  "Comfortable chewing",
  "Speech",
  "Bite stability",
  "Oral health",
  "Facial support",
  "Smile appearance",
  "Confidence",
  "Long-term durability of dental restorations",
];

const whyChooseDrK = [
  "Healthy",
  "Functional",
  "Natural-looking",
  "Comfortable",
  "Individually designed",
];

const faqItems = [
  {
    q: "How long does treatment take?",
    a: "Treatment time depends on the complexity of your case. Some patients complete treatment in a few months, while more complex rehabilitations involving implants or healing stages may take longer.",
  },
  {
    q: "Will I need dental implants?",
    a: "Not every patient requires implants. Your treatment plan will be based on your existing teeth, bone support, and long-term goals.",
  },
  {
    q: "Is treatment painful?",
    a: "Modern techniques and local anesthesia allow most procedures to be completed comfortably. Sedation options may also be available for patients who experience dental anxiety.",
  },
  {
    q: "Is full-mouth rehabilitation cosmetic or restorative?",
    a: "It is both. The primary goal is to restore health and function, while also improving the appearance of your smile.",
  },
];

export const FullMouthRehabilitationPage = () => {
  return (
    <div data-testid="full-mouth-rehabilitation-page">
      <Seo
        title="Full-Mouth Rehabilitation | Smiles by Dr. K"
        description="Comprehensive full-mouth rehabilitation to restore comfort, function, and confidence with personalized planning by Dr. K."
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            {
              name: "Full-Mouth Rehabilitation",
              path: "/full-mouth-rehabilitation",
            },
          ]),
        ]}
      />

      <section className="relative h-[40vh]  overflow-hidden">
        <img
            src=                 "https://galleria-dental-media.s3.eu-north-1.amazonaws.com/uploads/gallery/3cd7d8bb-ab02-4dce-a273-7d3e37006215.jpg"

            // "https://galleria-dental-media.s3.eu-north-1.amazonaws.com/uploads/gallery/8c0e2b8c-2038-4f5a-b254-6b9260a7e574.jpg"

          // src="https://galleria-dental-media.s3.eu-north-1.amazonaws.com/uploads/gallery/04fbf3e3-017e-48de-908b-c449a056db31.png"
          alt=""
          // className="absolute inset-0 w-full h-full object-cover object-center"
          className="absolute inset-0 w-full h-full object-cover scale-[1.75]"
        />
        {/* <div className="absolute inset-0 bg-ink/65" /> */}
      </section>

      <section className="py-10 container-x">
        <div className="label-eyebrow text-gold mb-6">
          Full-Mouth Rehabilitation
        </div>
        <h1 className="heading-display text-4xl md:text-6xl text-ink max-w-4xl">
          Rebuild Your Smile. Restore Your Confidence.
        </h1>
        <div className="max-w-4xl text-ink/80 leading-[1.85] space-y-6">
          <p>
            When multiple dental problems affect your smile, chewing ability, or
            comfort, treating one tooth at a time may not provide the best
            long-term solution. Full-mouth rehabilitation is a comprehensive
            treatment approach that restores the health, function, strength, and
            appearance of your entire mouth.
          </p>
          <p>
            At either one of Dr. K&apos;s practices, every rehabilitation is
            carefully planned around your unique facial features, bite, oral
            health, and long-term goals. The objective is not simply to repair
            teeth, but to create a balanced, healthy smile designed to function
            naturally for years to come.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-cream/50">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Overview"
              title="What Is"
              italicWord="Full-Mouth Rehabilitation?"
            />
          </div>
          <div className="lg:col-span-7 text-ink/80 leading-[1.8] space-y-6">
            <p>
              Full-mouth rehabilitation combines multiple restorative and
              cosmetic procedures into one comprehensive treatment plan. It is
              recommended when extensive damage, wear, missing teeth, or bite
              problems affect the overall health of the mouth.
            </p>
            <p>Treatment may include:</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {treatmentIncludes.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              Each treatment plan is fully customized. No two rehabilitations
              are exactly alike.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Assessment"
              title="Who Is"
              italicWord="a Candidate?"
            />
          </div>
          <div className="lg:col-span-7 text-ink/80 leading-[1.8] space-y-6">
            <p>You may benefit from full-mouth rehabilitation if you have:</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {candidateFor.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              Many patients have lived with these problems for years and simply
              assumed nothing could be done. Modern dentistry offers solutions
              that can often restore both comfort and confidence.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-ink text-bone">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Planning"
              title="Dr. K's"
              italicWord="Comprehensive Approach"
              light
            />
          </div>
          <div className="lg:col-span-7 leading-[1.8] space-y-6 text-white/85">
            <p>
              Every rehabilitation begins with careful diagnosis and treatment
              planning.
            </p>
            <p>Your evaluation may include:</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {evaluationIncludes.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              Using this information, Dr. K develops a step-by-step treatment
              plan designed to restore oral health while achieving natural
              aesthetics and proper function.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container-x">
        <div className="max-w-4xl text-ink/80 leading-[1.8] space-y-6">
          <SectionHeading
            eyebrow="Precision"
            title="Planning Before"
            italicWord="Treatment Begins"
          />
          <p>Complex dentistry should never rely on guesswork.</p>
          <p>
            Whenever appropriate, Digital Smile Design and digital treatment
            planning allow you to preview important aspects of your future smile
            before treatment begins. This planning process helps ensure that the
            final result complements your facial proportions, smile line, and
            bite while improving long-term function.
          </p>
          <p>
            Every detail is considered before the first procedure is performed.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-cream/40">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Outcomes"
              title="What Conditions"
              italicWord="Can Be Corrected?"
            />
          </div>
          <div className="lg:col-span-7 text-ink/80 leading-[1.8] space-y-6">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {correctedConditions.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              Rather than treating each issue separately, rehabilitation
              corrects the underlying causes to create a healthier, more stable
              result.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Benefits"
              title="Benefits of"
              italicWord="Full-Mouth Rehabilitation"
            />
          </div>
          <div className="lg:col-span-7 text-ink/80 leading-[1.8] space-y-6">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {rehabilitationBenefits.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              The goal is to create a smile that not only looks beautiful but
              also functions properly every day.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-ink text-bone">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Why Us"
              title="Why Choose"
              italicWord="Dr. K?"
              light
            />
          </div>
          <div className="lg:col-span-7 leading-[1.8] space-y-6 text-white/85">
            <p>
              Successful rehabilitation requires more than technical skill. It
              requires careful planning, attention to detail, and an artistic
              understanding of facial harmony to achieve a fully functional
              beautiful smile.
            </p>
            <p>
              Dr. K combines advanced restorative techniques with cosmetic
              principles to create smiles that are:
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {whyChooseDrK.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              Every decision is made with your long-term oral health in mind.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container-x">
        <SectionHeading
          eyebrow="FAQs"
          title="Frequently Asked"
          italicWord="Questions"
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          {faqItems.map((faq) => (
            <div key={faq.q} className="p-6 border border-ink/10 bg-white">
              <h3 className="font-serif text-2xl font-light text-ink">
                {faq.q}
              </h3>
              <p className="mt-3 text-sm leading-[1.8] text-ink/70">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.smileCenter}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-ink/75" />
        </div>
        <div className="relative container-x text-center text-white">
          <h2 className="heading-display text-4xl md:text-6xl max-w-4xl mx-auto">
            Begin Your{" "}
            <span className="italic text-gold-light">Smile Transformation</span>
          </h2>
          <p className="max-w-3xl mx-auto mt-6 text-white/80 leading-relaxed">
            A healthier smile begins with a comprehensive evaluation. If
            damaged, worn, or missing teeth have affected your ability to eat
            comfortably, smile confidently, or enjoy everyday life, full-mouth
            rehabilitation may provide a lasting solution.
          </p>
          <p className="max-w-3xl mx-auto mt-4 text-white/70 leading-relaxed">
            Schedule your consultation and discover how comprehensive,
            personalized dentistry can restore the strength, function, and
            beauty of your smile.
          </p>
          <a href={WEAVE_BOOKING_URL} onClick={openWeaveBookingInNewTab} className="btn-outline-light mt-10 inline-flex">
            Book a Consult
          </a>
        </div>
      </section>
    </div>
  );
};
